import { useEffect, useCallback, useState } from "react";
import { X, ExternalLink, FileText, Quote, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import type { GroundingSource } from "@/services/api";

interface CitationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The sources being displayed (grouped by URL) */
  sources: GroundingSource[];
  /** The citation index (1-based) */
  citationIndex: number;
  /** Callback to close the modal */
  onClose: () => void;
}

/**
 * Collapsible excerpt section — hidden by default.
 */
function ExcerptToggle({ snippet, label, formatSnippet }: { snippet: string; label?: string; formatSnippet: (t: string) => string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
      >
        <Quote className="w-4 h-4" />
        <span>{isOpen ? "Hide excerpt" : (label || "View excerpt")}</span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && (
        <div className="relative mt-2">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-full" />
          <blockquote className="pl-4 py-3 text-slate-700 leading-relaxed bg-amber-50/50 rounded-r-lg border-y border-r border-amber-100 whitespace-pre-wrap">
            {formatSnippet(snippet)}
          </blockquote>
        </div>
      )}
    </div>
  );
}

/**
 * Slide-over modal that displays citation source details.
 * Shows the source URL, title, and all text snippets from that page.
 */
export function CitationModal({ isOpen, sources, citationIndex, onClose }: CitationModalProps) {
  const primarySource = sources[0] ?? null;

  // Close on escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  /**
   * Get clean URL without query parameters for display.
   */
  const getCleanUrl = (uri: string): string => {
    try {
      const url = new URL(uri);
      return url.origin + url.pathname;
    } catch {
      return uri.split('?')[0].split('#')[0];
    }
  };

  /**
   * Check if a title looks like a Gemini internal file reference.
   */
  const isGeminiFileTitle = (title: string): boolean => {
    if (!title) return false;
    return /^[\w.-]+-\d{10,}\.md$/i.test(title) ||
           /\.md$/i.test(title) && /\d{10,}/.test(title);
  };

  /**
   * Generate a display title from the URL path.
   */
  const getTitleFromUrl = (uri: string): string | null => {
    try {
      const url = new URL(uri);
      const pathParts = url.pathname.split('/').filter(Boolean);
      if (pathParts.length === 0) return url.hostname;

      const lastPart = pathParts[pathParts.length - 1];
      return lastPart
        .replace(/[-_]/g, ' ')
        .replace(/\.[^.]+$/, '')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    } catch {
      return null;
    }
  };

  /**
   * Get the best display title for the source.
   */
  const getDisplayTitle = (title: string | undefined, uri: string): string | null => {
    if (!title || isGeminiFileTitle(title)) {
      return getTitleFromUrl(uri);
    }
    return title;
  };

  /**
   * Build a URL with a Text Fragment (#:~:text=...) so the browser
   * scrolls to and highlights the relevant section on the source page.
   */
  const getTextFragmentUrl = (uri: string, snippet?: string): string => {
    if (!snippet) return uri;

    const clean = snippet
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (clean.length < 10) return uri;

    const maxLen = 80;
    let textStart = clean.slice(0, maxLen);
    if (clean.length > maxLen) {
      const lastSpace = textStart.lastIndexOf(' ');
      if (lastSpace > 40) textStart = textStart.slice(0, lastSpace);
    }

    const base = uri.split('#')[0];
    return `${base}#:~:text=${encodeURIComponent(textStart)}`;
  };

  /**
   * Format snippet text by cleaning up markdown and HTML artifacts.
   */
  const formatSnippet = (text: string): string => {
    return text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/  +/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  // Collect all unique non-empty snippets
  const snippets = sources
    .map(s => s.snippet)
    .filter((s): s is string => Boolean(s));

  return (
    <AnimatePresence>
      {isOpen && primarySource && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-50"
            onClick={onClose}
          />

          {/* Slide-over panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 font-bold text-sm">
                  {citationIndex}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Source Citation</h2>
                  <p className="text-sm text-slate-500">
                    {snippets.length > 1
                      ? `${snippets.length} excerpts from this page`
                      : "Grounding reference"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Source URL */}
              <div className="mb-6">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-500 mb-2">
                  <FileText className="w-4 h-4" />
                  <span>Source Page</span>
                </div>
                <a
                  href={getTextFragmentUrl(primarySource.uri, snippets[0])}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {getDisplayTitle(primarySource.title, primarySource.uri) && (
                        <p className="font-medium text-slate-900 mb-1 group-hover:text-amber-700 transition-colors">
                          {getDisplayTitle(primarySource.title, primarySource.uri)}
                        </p>
                      )}
                      <p className="text-sm text-slate-500 truncate">
                        {getCleanUrl(primarySource.uri)}
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-amber-500 flex-shrink-0 mt-1" />
                  </div>
                </a>
              </div>

              {/* Excerpts — collapsible */}
              {snippets.length > 0 && (
                <div className="space-y-3">
                  {snippets.map((snippet, idx) => (
                    <ExcerptToggle
                      key={idx}
                      snippet={snippet}
                      label={snippets.length > 1 ? `View excerpt ${idx + 1}` : undefined}
                      formatSnippet={formatSnippet}
                    />
                  ))}
                </div>
              )}

              {/* Full URL */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <a
                  href={getTextFragmentUrl(primarySource.uri, snippets[0])}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-amber-600 hover:text-amber-700 break-all bg-slate-50 p-2 rounded hover:underline transition-colors"
                >
                  {primarySource.uri}
                </a>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Close
                </button>
                <a
                  href={getTextFragmentUrl(primarySource.uri, snippets[0])}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-4 py-2 text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors font-medium text-center inline-flex items-center justify-center gap-2"
                >
                  <span>View Source</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
